import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from '../../../services/notification.service';
import {
  CancellationLocation,
  CancellationSettings,
  CancellationSettingsData,
  CancellationSettingsSource,
  CancellationSettingsUpdatePayload,
} from '../../../services/cancellation-settings.service';

const OFF: CancellationSettings = {
  enabled: false,
  location_ids: [],
  prorated_refund_enabled: true,
  updated_by: null,
  updated_at: null,
};

/**
 * CWS-1776: the switch for cancel & refund (CWS-1534). Same shape and the
 * same save-on-every-toggle behaviour as the plan-change panel, plus the
 * PRD's separate Prorated refund switch. Renders whatever the server hands
 * back; the `source` input is how the super-admin company page points it at
 * one company's settings.
 */
@Component({
  selector: 'app-cancellation-settings',
  standalone: true,
  imports: [CommonModule],
  providers: [NotificationService],
  templateUrl: './cancellation-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cancellation-settings.component.scss'],
})
export class CancellationSettingsComponent implements OnChanges, OnDestroy {
  private readonly messageService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();
  /** Fires on every (re)load: a late answer for the previous `source` must never land here. */
  private readonly reload$ = new Subject<void>();

  @Input() isActive = false;
  @Input({ required: true }) source!: CancellationSettingsSource;
  /** Mirrors `isLoading` so the host can hold its reveal until this section has loaded. */
  @Output() loadingChange = new EventEmitter<boolean>();

  settings: CancellationSettings = OFF;
  locations: CancellationLocation[] = [];
  globallyEnabled = true;

  isLoading = false;
  isSaving = false;
  /** A failed load leaves the default OFF on screen; editing it would save a guess. */
  loadFailed = false;

  get isInteractionDisabled(): boolean {
    return this.isLoading || this.isSaving || this.loadFailed;
  }

  /** Empty allowlist means every location. */
  get isRestrictedToLocations(): boolean {
    return this.settings.location_ids.length > 0;
  }

  isLocationSelected(locationId: string): boolean {
    const wanted = String(locationId).toLowerCase();
    return this.settings.location_ids.some(id => id.toLowerCase() === wanted);
  }

  locationLabel(location: CancellationLocation): string {
    return location.branch || location.id;
  }

  trackLocation(_index: number, location: CancellationLocation): string {
    return location.id;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']?.currentValue === true || (this.isActive && changes['source'])) {
      this.loadSettings();
    }
  }

  ngOnDestroy(): void {
    this.reload$.next();
    this.reload$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadSettings();
  }

  onToggleEnabled(): void {
    if (this.isInteractionDisabled) {
      return;
    }
    this.save({ enabled: !this.settings.enabled, location_ids: this.settings.location_ids });
  }

  /** The PRD's separate switch: when off, Prorated is hidden at every entry point. */
  onToggleProrated(): void {
    if (this.isInteractionDisabled || !this.settings.enabled) {
      return;
    }
    this.save({
      enabled: this.settings.enabled,
      location_ids: this.settings.location_ids,
      prorated_refund_enabled: !this.settings.prorated_refund_enabled,
    });
  }

  /**
   * Unrestricted shows every location ticked, so the first untick names every
   * other location explicitly. Unticking the last remaining location switches
   * the feature off: an empty allowlist would mean "every location", the
   * opposite of what was asked.
   */
  onToggleLocation(locationId: string): void {
    if (this.isInteractionDisabled || !this.settings.enabled) {
      return;
    }
    const wanted = String(locationId).toLowerCase();
    const current = this.isRestrictedToLocations
      ? this.settings.location_ids.map(id => id.toLowerCase())
      : this.locations.map(l => String(l.id).toLowerCase());
    const next = current.includes(wanted)
      ? current.filter(id => id !== wanted)
      : [...current, wanted];
    if (next.length === 0) {
      this.save({ enabled: false, location_ids: [] });
      return;
    }
    const everyLocation =
      this.locations.length > 0 &&
      this.locations.every(l => next.includes(String(l.id).toLowerCase()));
    this.save({ enabled: true, location_ids: everyLocation ? [] : next });
  }

  onAllowEveryLocation(): void {
    if (this.isInteractionDisabled || !this.isRestrictedToLocations) {
      return;
    }
    this.save({ enabled: this.settings.enabled, location_ids: [] });
  }

  private applyData(data: CancellationSettingsData | null | undefined): void {
    this.settings = data?.settings ?? OFF;
    this.locations = data?.locations ?? [];
    this.globallyEnabled = data?.globally_enabled !== false;
  }

  private setLoading(loading: boolean): void {
    if (this.isLoading === loading) {
      return;
    }
    this.isLoading = loading;
    this.loadingChange.emit(loading);
  }

  private loadSettings(): void {
    // Cancel whatever the previous source still had in flight, load or save,
    // and show nothing of it: this panel now belongs to the new source.
    this.reload$.next();
    this.applyData(null);
    this.loadFailed = false;
    this.setLoading(true);
    this.source
      .getSettings()
      .pipe(
        takeUntil(this.reload$),
        takeUntil(this.destroy$),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Load failed',
            detail: err.error?.message || 'Could not load cancellation settings.',
          });
          return of(null);
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe(res => {
        if (res == null) {
          this.loadFailed = true;
          return;
        }
        if (res.status === false) {
          this.loadFailed = true;
          this.messageService.add({
            severity: 'error',
            summary: 'Load failed',
            detail: res.message || 'Could not load cancellation settings.',
          });
          return;
        }
        this.applyData(res.data);
      });
  }

  private savedDetail(): string {
    if (!this.settings.enabled) {
      return 'Cancel and refund is off.';
    }
    const where = this.isRestrictedToLocations
      ? `${this.settings.location_ids.length} location(s)`
      : 'every location';
    const prorated = this.settings.prorated_refund_enabled
      ? 'Prorated refunds offered.'
      : 'Prorated refunds hidden.';
    return `Cancel and refund is on at ${where}. ${prorated}`;
  }

  private save(payload: CancellationSettingsUpdatePayload): void {
    this.isSaving = true;
    this.source
      .updateSettings(payload)
      .pipe(
        takeUntil(this.reload$),
        takeUntil(this.destroy$),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: err.error?.message || 'Could not save cancellation settings.',
          });
          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(res => {
        if (res == null) {
          return;
        }
        if (res.status === false) {
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: res.message || 'The server did not save the settings.',
          });
          return;
        }
        this.applyData(res.data);
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: this.savedDetail(),
        });
      });
  }
}
